// ═══════════════════════════════════════════════════════════════════════════
// Block Authoring — strict AutoCAD-spec-aligned data model (Phase K.5)
// ═══════════════════════════════════════════════════════════════════════════
//
// This module mirrors the DXF group-code structure of AutoCAD's dynamic
// block authoring objects (BLOCK<KIND>PARAMETER, BLOCK<KIND>ACTION,
// BLOCKVISIBILITYSTATE inline records, ACAD_EVALUATION_GRAPH topology)
// per the spec captured in `docs/autocad-parity/block-authoring-spec.md`.
//
// **Why a parallel schema** to the existing `dynamicBlock*.ts`?
//
//  - The existing `DynamicBlockMetadata` (drawing-model/dynamicBlock.ts)
//    is an evaluator-friendly normalisation: it re-keys fields by
//    semantic name (`currentDistance`, `defaultAngle`, `valueSet.mode`)
//    so the block-evaluator and UI layer can consume them uniformly.
//
//  - This `BlockAuthoringData` is the *DXF source of truth*: every
//    field maps to a specific group code on a specific subclass marker
//    (AcDbBlockLinearParameter 305 → label, 140 → distance, …).
//    Round-tripping through this layer guarantees the writer can emit
//    the bytes AutoCAD expects.
//
// The two layers compose: bridge code (dxfBridge.entityConversion) reads
// `BlockAuthoringData` from DXF, then upcasts to `DynamicBlockMetadata`
// for evaluator consumption; on save, evaluator state is mapped back into
// authoring data and re-serialised verbatim.
//
// Field naming follows AutoCAD spec conventions:
//   - point fields end in `Point` (basePoint, endPoint, gripPosition, …)
//   - distance/angle property names match AutoCAD's delta naming
//     (`distancePropertyName = "EndXDelta"`, etc.) so writers can
//     emit them on group code 303/304.
//   - guid carries the `{XXXXXXXX-...}` braces verbatim.
//
// ─── Reference ───────────────────────────────────────────────────────
//   docs/autocad-parity/block-authoring-spec.md §4 (DXF templates)
//   tmp_dxf/linear_only.dxf (1 LINEAR param + MOVE action minimum)
//   tmp_dxf/arch_metric_full.dxf (full set: Linear/Flip/Visibility/
//                                  Alignment/Basepoint/Lookup/Rotation,
//                                  Move/Scale/Stretch/Flip/Rotate/
//                                  Array/Lookup actions)

import { z } from "zod";

// ─── Point ───────────────────────────────────────────────────────────
// Authoring uses 3D points (group 1010/1020/1030 etc.) but the Z is
// almost always 0 for 2D-authored blocks. We retain Z so float-precise
// round-trip works for the rare 3D-authored case.
export const authoringPointSchema = z.object({
  x: z.number(),
  y: z.number(),
  z: z.number().default(0),
});
export type AuthoringPoint = z.infer<typeof authoringPointSchema>;

// ─── Common parameter base ───────────────────────────────────────────
//
// Every BLOCK<KIND>PARAMETER carries an `AcDbBlockElement` subclass with
// the parameter's display name (group 300) and an `AcDbBlockParameter`
// subclass with the base-location flag (280) and value-set flag (281).
// `nodeId` is the eval-graph node id (group 90 on AcDbEvalExpr) — needed
// to wire the topology back together on write.
export const baseAuthoringParameterSchema = z.object({
  // Stable id used by actions to reference this parameter; usually the
  // DXF handle but synthesised when authoring fresh.
  id: z.string().min(1),
  // DXF handle (group 5). Empty for synthesised parameters.
  handle: z.string().default(""),
  // Owner handle (group 330) → AcDbEvalGraph that owns this parameter.
  evalGraphHandle: z.string().default(""),
  // EvalExpr node id (group 90). 0 means "not yet assigned".
  nodeId: z.number().int().default(0),
  // AcDbBlockElement.300 — display / palette name (often Russian
  // localised in real fixtures). Stored as Unicode; the writer
  // re-encodes to MBCS on emit per AutoCAD's mojibake convention.
  name: z.string().default(""),
  // AcDbBlockElement.1071 — palette show flag (1 = show, 0 = hide).
  paletteShow: z.boolean().default(true),
  // AcDbBlockParameter.280 — base location anchor:
  //   0 = startpoint, 1 = midpoint (Linear/Polar/XY default).
  baseLocation: z.number().int().default(0),
  // AcDbBlockParameter.281 — value-set flag (0 = none, 1 = increment,
  // 2 = list — same enumeration as the typed `valueSet.mode`).
  valueSetFlag: z.number().int().default(0),
});

// ─── Value set (Increment / List) ─────────────────────────────────────
//
// Linear / Polar / XY / Rotation parameters expose a value-set
// constraint that snaps the grip drag. AutoCAD stores it on the
// type-specific subclass (AcDbBlockLinearParameter et al) at fixed
// group codes per the spec.
export const authoringValueSetSchema = z.object({
  mode: z.enum(["none", "increment", "list"]).default("none"),
  // For "increment" mode.
  min: z.number().default(0),
  max: z.number().default(0),
  increment: z.number().default(0),
  // For "list" mode — explicit allowed values.
  listValues: z.array(z.number()).default([]),
});
export type AuthoringValueSet = z.infer<typeof authoringValueSetSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Parameter kinds (10 total per AutoCAD spec)
// ═══════════════════════════════════════════════════════════════════════════

// ─── 1. Point Parameter ──────────────────────────────────────────────
//
// AcDbBlock1PtParameter + AcDbBlockPointParameter
// One anchor point (1010/1020/1030); user drags to offset.
// Outputs: UpdatedX, UpdatedY (2 deltas).
export const pointAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("point"),
  basePoint: authoringPointSchema,
  // 305 — display label (AcDbBlockPointParameter)
  label: z.string().default(""),
  // 306 — description / tooltip
  description: z.string().default(""),
  // 96 — output node id for the X delta
  xOutputNodeId: z.number().int().default(0),
  // 97 — output node id for the Y delta
  yOutputNodeId: z.number().int().default(0),
});
export type PointAuthoringParameter = z.infer<typeof pointAuthoringParameterSchema>;

// ─── 2. Linear Parameter ─────────────────────────────────────────────
//
// AcDbBlock2PtParameter (start + end) + AcDbBlockLinearParameter.
// 1010/1020/1030 = startPoint, 1011/1021/1031 = endPoint.
// 305 = label, 140 = distance (default), 141/142/143 = value-set bounds.
export const linearAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("linear"),
  startPoint: authoringPointSchema,
  endPoint: authoringPointSchema,
  // AcDbBlockLinearParameter.305
  label: z.string().default(""),
  // AcDbBlockLinearParameter.306
  description: z.string().default(""),
  // AcDbBlockLinearParameter.140 — current/default distance
  distance: z.number().default(0),
  // AcDbBlockLinearParameter.307 — unit format string (empty = default)
  unitFormat: z.string().default(""),
  // AcDbBlockLinearParameter.96 — input node id (links from grip).
  inputNodeId: z.number().int().default(0),
  // Property names used in evaluator deltas (AcDbBlock2PtParameter
  // 303/304 = DisplacementX/DisplacementY).
  displacementXProperty: z.string().default("DisplacementX"),
  displacementYProperty: z.string().default("DisplacementY"),
  valueSet: authoringValueSetSchema.default(() => authoringValueSetSchema.parse({})),
});
export type LinearAuthoringParameter = z.infer<typeof linearAuthoringParameterSchema>;

// ─── 3. Polar Parameter ──────────────────────────────────────────────
//
// AcDbBlock2PtParameter + AcDbBlockPolarParameter.
// Like Linear but exposes both distance AND angle outputs.
// 50 = default angle (radians), 51 = current angle.
export const polarAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("polar"),
  startPoint: authoringPointSchema,
  endPoint: authoringPointSchema,
  label: z.string().default(""),
  description: z.string().default(""),
  // 140 = default distance, 141 = current distance
  distance: z.number().default(0),
  // 50 = default angle, 51 = current angle (radians)
  angle: z.number().default(0),
  unitFormat: z.string().default(""),
  inputNodeId: z.number().int().default(0),
  distanceValueSet: authoringValueSetSchema.default(() =>
    authoringValueSetSchema.parse({}),
  ),
  angleValueSet: authoringValueSetSchema.default(() =>
    authoringValueSetSchema.parse({}),
  ),
});
export type PolarAuthoringParameter = z.infer<typeof polarAuthoringParameterSchema>;

// ─── 4. XY Parameter ─────────────────────────────────────────────────
//
// Like Linear but X/Y deltas vary independently. Used by 2D Array
// actions (column × row spacing).
export const xyAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("xy"),
  startPoint: authoringPointSchema,
  endPoint: authoringPointSchema,
  label: z.string().default(""),
  description: z.string().default(""),
  // 140 / 141 — default X / current X
  distanceX: z.number().default(0),
  // 142 / 143 — default Y / current Y
  distanceY: z.number().default(0),
  unitFormat: z.string().default(""),
  inputNodeId: z.number().int().default(0),
  xValueSet: authoringValueSetSchema.default(() =>
    authoringValueSetSchema.parse({}),
  ),
  yValueSet: authoringValueSetSchema.default(() =>
    authoringValueSetSchema.parse({}),
  ),
});
export type XYAuthoringParameter = z.infer<typeof xyAuthoringParameterSchema>;

// ─── 5. Rotation Parameter ───────────────────────────────────────────
//
// AcDbBlock2PtParameter (base + ref) + AcDbBlockRotationParameter.
// 1010 = base, 1011 = ref point, 140 = default angle (rad).
export const rotationAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("rotation"),
  basePoint: authoringPointSchema,
  refPoint: authoringPointSchema,
  label: z.string().default(""),
  description: z.string().default(""),
  // 140 = default angle (rad), 141 = current angle
  angle: z.number().default(0),
  // 142 = min, 143 = max, 144 = increment (value-set)
  unitFormat: z.string().default(""),
  inputNodeId: z.number().int().default(0),
  angleValueSet: authoringValueSetSchema.default(() =>
    authoringValueSetSchema.parse({}),
  ),
});
export type RotationAuthoringParameter = z.infer<typeof rotationAuthoringParameterSchema>;

// ─── 6. Alignment Parameter ──────────────────────────────────────────
//
// AcDbBlock2PtParameter + AcDbBlockAlignmentParameter.
// 280: 0 = perpendicular, 1 = tangent.
// Built-in: rotates the entire block; no action object needed.
export const alignmentAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("alignment"),
  basePoint: authoringPointSchema,
  alignmentDirection: authoringPointSchema,
  // AcDbBlockAlignmentParameter.280: 0 = perpendicular, 1 = tangent
  alignmentType: z.enum(["perpendicular", "tangent"]).default("perpendicular"),
});
export type AlignmentAuthoringParameter = z.infer<typeof alignmentAuthoringParameterSchema>;

// ─── 7. Flip Parameter ───────────────────────────────────────────────
//
// AcDbBlock2PtParameter (mirror line endpoints) + AcDbBlockFlipParameter.
// 305 = name, 306 = description, 307/308 = state labels (Front/Back).
// 1012/1022/1032 = grip display position (offset from line).
// 309 = delta name (UpdatedFlip).
export const flipAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("flip"),
  reflectionLineStart: authoringPointSchema,
  reflectionLineEnd: authoringPointSchema,
  label: z.string().default(""),
  description: z.string().default(""),
  // 307 = "Not flipped" label (state 0)
  baseStateLabel: z.string().default("Base"),
  // 308 = "Flipped" label (state 1)
  flippedStateLabel: z.string().default("Flipped"),
  gripPosition: authoringPointSchema.optional(),
  // 309 — output delta name (default "UpdatedFlip")
  flipPropertyName: z.string().default("UpdatedFlip"),
  // 96 — output node id
  outputNodeId: z.number().int().default(0),
  // current value (0 = base, 1 = flipped)
  flipState: z.number().int().min(0).max(1).default(0),
});
export type FlipAuthoringParameter = z.infer<typeof flipAuthoringParameterSchema>;

// ─── 8. Visibility Parameter ─────────────────────────────────────────
//
// AcDbBlock1PtParameter + AcDbBlockVisibilityParameter.
// Inline storage of all states + per-state visible-entity handle lists
// per spec §3.1 (groups 93/331 × N, 95/333/303/94/332 × S).
export const authoringVisibilityStateSchema = z.object({
  // 303 — state name
  name: z.string().default(""),
  // 332 — handles of master block entities VISIBLE in this state
  visibleEntityHandles: z.array(z.string()).default([]),
  // 333 — repeated state-grip refs (typically 2 per state)
  stateGripHandles: z.array(z.string()).default([]),
});
export type AuthoringVisibilityState = z.infer<typeof authoringVisibilityStateSchema>;

export const visibilityAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("visibility"),
  gripPosition: authoringPointSchema,
  // 301 — current state name (display)
  currentStateName: z.string().default(""),
  // 302 — description / tooltip
  description: z.string().default(""),
  // 91 — active state index (0-based)
  activeStateIndex: z.number().int().default(0),
  // 93 — total master entity count (== masterEntityHandles.length)
  // 331 — master block entity handles (every entity tracked)
  masterEntityHandles: z.array(z.string()).default([]),
  // 95 — state count
  // states[]: ordered list per spec §3.1
  states: z.array(authoringVisibilityStateSchema).default([]),
});
export type VisibilityAuthoringParameter = z.infer<typeof visibilityAuthoringParameterSchema>;

// ─── 9. Lookup Parameter ─────────────────────────────────────────────
//
// AcDbBlock1PtParameter + AcDbBlockLookUpParameter (note camelCase).
// The parameter itself just labels and points at a lookup row;
// the actual table is on the linked BLOCKLOOKUPACTION.
export const lookupAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("lookup"),
  gripPosition: authoringPointSchema,
  label: z.string().default(""),
  description: z.string().default(""),
  // 94 — input node id
  inputNodeId: z.number().int().default(0),
  // The current selected lookup row label (set by user; mirrors
  // BLOCKLOOKUPACTION's output column).
  currentLookupValue: z.string().default(""),
});
export type LookupAuthoringParameter = z.infer<typeof lookupAuthoringParameterSchema>;

// ─── 10. Base Point Parameter ────────────────────────────────────────
//
// AcDbBlock1PtParameter + AcDbBlockBasepointParameter.
// 1010 = current base point, 1011 = static reference, 1012 = runtime
// updated base point. No action; can be selected by other actions to
// translate alongside their geometry.
export const basepointAuthoringParameterSchema = baseAuthoringParameterSchema.extend({
  kind: z.literal("basepoint"),
  basePoint: authoringPointSchema,
  // 1011 — static reference point (typically same as basePoint at author)
  referencePoint: authoringPointSchema,
  // 1012 — runtime / updated base point
  updatedBasePoint: authoringPointSchema,
});
export type BasepointAuthoringParameter = z.infer<typeof basepointAuthoringParameterSchema>;

// ─── Parameter discriminated union ───────────────────────────────────
export const blockAuthoringParameterSchema = z.discriminatedUnion("kind", [
  pointAuthoringParameterSchema,
  linearAuthoringParameterSchema,
  polarAuthoringParameterSchema,
  xyAuthoringParameterSchema,
  rotationAuthoringParameterSchema,
  alignmentAuthoringParameterSchema,
  flipAuthoringParameterSchema,
  visibilityAuthoringParameterSchema,
  lookupAuthoringParameterSchema,
  basepointAuthoringParameterSchema,
]);
export type BlockAuthoringParameter = z.infer<typeof blockAuthoringParameterSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Action kinds (8 total per AutoCAD spec)
// ═══════════════════════════════════════════════════════════════════════════

// ─── Common action base ──────────────────────────────────────────────
//
// AcDbBlockAction subclass: 70 = flags (bit 0 = chain editing), 71 =
// selection count, 330 × N = entity handle refs, 1010/1020/1030 =
// action display anchor (where the action label shows in BEDIT).
export const baseAuthoringActionSchema = z.object({
  id: z.string().min(1),
  handle: z.string().default(""),
  evalGraphHandle: z.string().default(""),
  nodeId: z.number().int().default(0),
  // AcDbBlockElement.300 — display name (mojibake-encoded in fixtures).
  name: z.string().default(""),
  // AcDbBlockAction.70 — flags (bit 0 = chain edit)
  flags: z.number().int().default(0),
  // AcDbBlockAction.71 — selection count (== entityHandles.length)
  // AcDbBlockAction.330 — entity handles in selection set
  entityHandles: z.array(z.string()).default([]),
  // 1010/1020/1030 — display anchor (where action label appears)
  displayAnchor: authoringPointSchema,
  // Linkage to driving parameter (resolved via AcDbEvalGraph topology).
  // Empty when the action is orphaned (rare, malformed DXF).
  parameterId: z.string().default(""),
});

// ─── 1. Move Action ──────────────────────────────────────────────────
//
// AcDbBlockMoveAction. 92 = X input id, 93 = Y input id, 301/302 =
// EndXDelta / EndYDelta (delta property names). 140 = scale factor,
// 141 = angle offset (radians), 280 = chain flag.
export const moveAuthoringActionSchema = baseAuthoringActionSchema.extend({
  kind: z.literal("move"),
  // AcDbBlockMoveAction.92 — X input node id
  xInputNodeId: z.number().int().default(0),
  // AcDbBlockMoveAction.93 — Y input node id
  yInputNodeId: z.number().int().default(0),
  // 301 / 302 — delta property names ("EndXDelta" / "EndYDelta")
  xDeltaProperty: z.string().default("EndXDelta"),
  yDeltaProperty: z.string().default("EndYDelta"),
  // 140 — scale factor (default 1.0)
  scaleFactor: z.number().default(1),
  // 141 — angle offset (radians)
  angleOffset: z.number().default(0),
});
export type MoveAuthoringAction = z.infer<typeof moveAuthoringActionSchema>;

// ─── 2. Scale Action ─────────────────────────────────────────────────
//
// AcDbBlockActionWithBasePt + AcDbBlockScaleAction.
// 92/93 input counts, 301/302 = UpdatedBaseX/UpdatedBaseY,
// 1011 = base point default, 1012 = base point runtime, 280 flag.
// On AcDbBlockScaleAction: 94 = scale-X input, 95 = scale-Y input,
// 96 = combined input, 303/304/305 = Scale/XScale/YScale, 280 = lock.
export const scaleAuthoringActionSchema = baseAuthoringActionSchema.extend({
  kind: z.literal("scale"),
  // ActionWithBasePt fields:
  basePoint: authoringPointSchema,
  updatedBasePoint: authoringPointSchema,
  baseInputCountA: z.number().int().default(1),
  baseInputCountB: z.number().int().default(1),
  baseUpdateXProperty: z.string().default("UpdatedBaseX"),
  baseUpdateYProperty: z.string().default("UpdatedBaseY"),
  // ScaleAction.94 / 95 / 96 — scale input node ids
  scaleXInputId: z.number().int().default(0),
  scaleYInputId: z.number().int().default(0),
  combinedScaleInputId: z.number().int().default(0),
  // 303/304/305 — property names
  scaleProperty: z.string().default("Scale"),
  xScaleProperty: z.string().default("XScale"),
  yScaleProperty: z.string().default("YScale"),
  // 280: 0 = independent X/Y, 1 = uniform (locked)
  scaleType: z.enum(["independent", "uniform", "x-only", "y-only"]).default("uniform"),
});
export type ScaleAuthoringAction = z.infer<typeof scaleAuthoringActionSchema>;

// ─── 3. Stretch Action ───────────────────────────────────────────────
//
// AcDbBlockStretchAction (most complex). Two-part selection:
//   - Crossing fence polygon (group 72 N + N pairs of 1011/1021)
//   - Secondary entity selection (group 73 N + N × 331 handle, 74 N
//     axis flags, 94 N axis_id values).
// 92 = X input id, 93 = Y input id, 301/302 = EndXDelta / EndYDelta.
// 140 = scale, 141 = angle, 280 = flag.
export const stretchAxisFlagSchema = z.object({
  // 74 — axis flag (always 1 in observed fixtures)
  flag: z.number().int().default(1),
  // 94 — axis_id (0 = X-only, 1 = Y-only, 2 = both)
  axisId: z.number().int().default(2),
});
export type StretchAxisFlag = z.infer<typeof stretchAxisFlagSchema>;

export const stretchAuthoringActionSchema = baseAuthoringActionSchema.extend({
  kind: z.literal("stretch"),
  // 92 — X input id
  xInputNodeId: z.number().int().default(0),
  // 301 — X delta property name
  xDeltaProperty: z.string().default("EndXDelta"),
  // 93 — Y input id
  yInputNodeId: z.number().int().default(0),
  // 302 — Y delta property name
  yDeltaProperty: z.string().default("EndYDelta"),
  // 72 + 1011/1021 pairs — crossing fence polygon
  crossingFence: z.array(authoringPointSchema).default([]),
  // 73 + 331 handles — secondary selection (full-translate entities).
  // We reuse `entityHandles` (from AcDbBlockAction) for the primary
  // selection. Secondary is the ENT_H list specifically referenced by
  // 331 within AcDbBlockStretchAction subclass (post-72 fence).
  secondaryEntityHandles: z.array(z.string()).default([]),
  // 74 + 94 — per-secondary-entity axis flags. Length should match
  // secondaryEntityHandles.length when DXF is well-formed.
  axisFlags: z.array(stretchAxisFlagSchema).default([]),
  // 75 — reserved (always 0 in observed fixtures)
  reservedFlag: z.number().int().default(0),
  // 140 — scale factor
  scaleFactor: z.number().default(1),
  // 141 — angle offset (radians)
  angleOffset: z.number().default(0),
});
export type StretchAuthoringAction = z.infer<typeof stretchAuthoringActionSchema>;

// ─── 4. Polar Stretch Action ─────────────────────────────────────────
//
// AcDbBlockPolarStretchAction — stretch along polar radius + rotate
// the rotation set around the polar base. Most complex action class.
// **Not present in arch_metric_full.dxf fixture.** Schema captures
// the AutoCAD spec verbatim so writers can emit valid DXF when
// authored programmatically.
export const polarStretchAuthoringActionSchema = baseAuthoringActionSchema.extend({
  kind: z.literal("polar-stretch"),
  // Like Stretch:
  xInputNodeId: z.number().int().default(0),
  yInputNodeId: z.number().int().default(0),
  xDeltaProperty: z.string().default("EndXDelta"),
  yDeltaProperty: z.string().default("EndYDelta"),
  crossingFence: z.array(authoringPointSchema).default([]),
  secondaryEntityHandles: z.array(z.string()).default([]),
  axisFlags: z.array(stretchAxisFlagSchema).default([]),
  reservedFlag: z.number().int().default(0),
  scaleFactor: z.number().default(1),
  angleOffset: z.number().default(0),
  // PolarStretch-specific: rotation pivot set (entities that rotate
  // as polar parameter angle changes).
  rotationEntityHandles: z.array(z.string()).default([]),
});
export type PolarStretchAuthoringAction = z.infer<typeof polarStretchAuthoringActionSchema>;

// ─── 5. Rotation Action ──────────────────────────────────────────────
//
// AcDbBlockActionWithBasePt + AcDbBlockRotationAction (note: action
// class is `Rotation`, DXF type is `BLOCKROTATEACTION`).
export const rotationAuthoringActionSchema = baseAuthoringActionSchema.extend({
  kind: z.literal("rotation"),
  basePoint: authoringPointSchema,
  updatedBasePoint: authoringPointSchema,
  baseInputCountA: z.number().int().default(1),
  baseInputCountB: z.number().int().default(1),
  baseUpdateXProperty: z.string().default("UpdatedBaseX"),
  baseUpdateYProperty: z.string().default("UpdatedBaseY"),
  // 92 — angle input id
  angleInputId: z.number().int().default(0),
  // 301 — angle delta property name (e.g. "AngleDelta")
  angleDeltaProperty: z.string().default("AngleDelta"),
});
export type RotationAuthoringAction = z.infer<typeof rotationAuthoringActionSchema>;

// ─── 6. Flip Action ──────────────────────────────────────────────────
//
// AcDbBlockFlipAction — minimal. Stores the input id and the
// UpdatedFlip property name; mirrors entities across the parameter's
// reflection line when parameter.flipState toggles.
export const flipAuthoringActionSchema = baseAuthoringActionSchema.extend({
  kind: z.literal("flip"),
  // 92 — flip input id
  flipInputId: z.number().int().default(0),
  // 301 — flip delta property name
  flipDeltaProperty: z.string().default("UpdatedFlip"),
});
export type FlipAuthoringAction = z.infer<typeof flipAuthoringActionSchema>;

// ─── 7. Array Action ─────────────────────────────────────────────────
//
// AcDbBlockArrayAction. 92/93/94/95 = input ids,
// 301/302/303/304 = Base/End/UpdatedBase/UpdatedEnd, 140 = column
// distance, 141 = row distance.
export const arrayAuthoringActionSchema = baseAuthoringActionSchema.extend({
  kind: z.literal("array"),
  // 92/93/94/95 — input node ids
  inputNodeIds: z.array(z.number().int()).default([0, 0, 0, 0]),
  // 301/302/303/304 — property names
  baseProperty: z.string().default("Base"),
  endProperty: z.string().default("End"),
  updatedBaseProperty: z.string().default("UpdatedBase"),
  updatedEndProperty: z.string().default("UpdatedEnd"),
  // 140 — column distance
  columnDistance: z.number().default(0),
  // 141 — row distance
  rowDistance: z.number().default(0),
});
export type ArrayAuthoringAction = z.infer<typeof arrayAuthoringActionSchema>;

// ─── 8. Lookup Action ────────────────────────────────────────────────
//
// AcDbBlockLookupAction. Stores a 2-column lookup table:
//   - 92 = input row count
//   - 93 = column count (= 2)
//   - 301 = column header (often empty)
//   - 302 × (rows × cols) = cell values (input rows then output rows)
//   - per-column metadata: 303 label, 94 input node id, 95/96 flags,
//     282 column type, 305 type str ("Custom" / "Lookup"), 281
//     flag, 304 delta name.
//   - 280 = closing flag (always 1).
export const lookupColumnSchema = z.object({
  // 303 — column label
  label: z.string().default(""),
  // 94 — input node id (parameter to update)
  inputNodeId: z.number().int().default(0),
  // 95 — primary flag (40 = read-only, varies)
  flag1: z.number().int().default(0),
  // 96 — secondary flag
  flag2: z.number().int().default(0),
  // 282 — column type: 0 = input column, 1 = output column
  columnType: z.number().int().default(0),
  // 305 — type string ("Custom" or "Lookup")
  typeString: z.string().default("Custom"),
  // 281 — duplicate flag (mirrors 282 in observed fixtures)
  typeFlag: z.number().int().default(0),
  // 304 — delta property name (e.g. "UpdatedDistance" or "lookupString")
  deltaProperty: z.string().default(""),
});
export type LookupColumn = z.infer<typeof lookupColumnSchema>;

export const lookupAuthoringActionSchema = baseAuthoringActionSchema.extend({
  kind: z.literal("lookup"),
  // 92 — number of input rows (row count)
  rowCount: z.number().int().default(0),
  // 93 — column count (typically 2: input + output)
  columnCount: z.number().int().default(2),
  // 301 — header (typically empty string)
  header: z.string().default(""),
  // Cell values, row-major: rows[i][j] = j-th column of i-th row.
  // Each cell is a string (input rows often hold numeric formatted as
  // "89.00000000000000"; output rows hold labels like "50 x 100").
  cells: z.array(z.array(z.string())).default([]),
  // Per-column metadata.
  columns: z.array(lookupColumnSchema).default([]),
  // 280 — closing flag (always 1 in observed fixtures)
  closingFlag: z.number().int().default(1),
});
export type LookupAuthoringAction = z.infer<typeof lookupAuthoringActionSchema>;

// ─── Action discriminated union ──────────────────────────────────────
export const blockAuthoringActionSchema = z.discriminatedUnion("kind", [
  moveAuthoringActionSchema,
  scaleAuthoringActionSchema,
  stretchAuthoringActionSchema,
  polarStretchAuthoringActionSchema,
  rotationAuthoringActionSchema,
  flipAuthoringActionSchema,
  arrayAuthoringActionSchema,
  lookupAuthoringActionSchema,
]);
export type BlockAuthoringAction = z.infer<typeof blockAuthoringActionSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Eval Graph topology (AcDbEvalGraph)
// ═══════════════════════════════════════════════════════════════════════════
//
// Per spec §4.4, the AcDbEvalGraph object exposes:
//   96 N    — node count
//   97 E    — edge count
//   for each node:
//     91 nodeId
//     93 typeFlag (32 = standard)
//     95 paramInputId
//     360 childObjectHandle
//     92 in_a, 92 in_b, 92 out_a, 92 out_b
//
// The "edges" between actions and parameters are encoded as the per-
// node 92 values (4 each) — node A points to node B if any of A's
// outgoing edge slots equals B's id.

export const evalGraphNodeSchema = z.object({
  nodeId: z.number().int(),
  typeFlag: z.number().int().default(32),
  paramInputId: z.number().int().default(0),
  // 360 — child object DXF handle (parameter / action / grip).
  childHandle: z.string().default(""),
  // 92 × 4 — incoming/outgoing edge slots. -1 = unused.
  incoming: z.tuple([z.number().int(), z.number().int()]).default([-1, -1]),
  outgoing: z.tuple([z.number().int(), z.number().int()]).default([-1, -1]),
});
export type EvalGraphNode = z.infer<typeof evalGraphNodeSchema>;

export const evalGraphEdgeSchema = z.object({
  fromNodeId: z.number().int(),
  toNodeId: z.number().int(),
});
export type EvalGraphEdge = z.infer<typeof evalGraphEdgeSchema>;

export const evalGraphSchema = z.object({
  // DXF handle of the AcDbEvalGraph object.
  handle: z.string().default(""),
  // Owner handle (the BLOCK_RECORD's xdict).
  ownerHandle: z.string().default(""),
  // 96 — node count (== nodes.length)
  // 97 — edge count (== edges.length)
  nodes: z.array(evalGraphNodeSchema).default([]),
  edges: z.array(evalGraphEdgeSchema).default([]),
});
export type EvalGraph = z.infer<typeof evalGraphSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Visibility states (top-level; mirrors VisibilityAuthoringParameter.states
// for blocks that author multiple visibility configurations independently
// of the parameter — rare, but allowed by the spec).
// ═══════════════════════════════════════════════════════════════════════════

export const blockVisibilityStateSchema = z.object({
  name: z.string().min(1),
  visibleEntityIds: z.array(z.string()).default([]),
});
export type BlockVisibilityState = z.infer<typeof blockVisibilityStateSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Block authoring data (top-level container on BlockDefinition)
// ═══════════════════════════════════════════════════════════════════════════
//
// This is what attaches to BlockDefinition.authoringData. Captures the
// *DXF source of truth* for the block's authoring graph plus the
// BLOCK_RECORD-level XDATA (true name, GUID).

export const blockAuthoringDataSchema = z.object({
  // BLOCK_RECORD XDATA: AcDbDynamicBlockTrueName (1000 string after
  // the AppID 1001 marker). The block's user-facing name; the
  // BLOCK_RECORD's own `2` group code holds the internal `*Un` name.
  trueName: z.string().optional(),
  // BLOCK_RECORD XDATA: AcDbDynamicBlockGUID (1000 string, "{...}").
  guid: z.string().optional(),
  // BLOCK_RECORD XDATA: AcDbBlockRepETag (1070 number, 1071 number).
  // Stable identifier for the cached block representation.
  repETag: z.tuple([z.number(), z.number()]).optional(),
  // Full set of parameters in authored order.
  parameters: z.array(blockAuthoringParameterSchema).default([]),
  // Full set of actions in authored order.
  actions: z.array(blockAuthoringActionSchema).default([]),
  // Top-level visibility states (rare; usually inline in
  // VisibilityAuthoringParameter.states). Empty when all visibility
  // is parameter-driven.
  visibilityStates: z.array(blockVisibilityStateSchema).default([]),
  // The eval graph that knits parameters → actions → grip components
  // together. Empty when block is static (no authoring).
  evalGraph: evalGraphSchema.default(() => evalGraphSchema.parse({})),
});
export type BlockAuthoringData = z.infer<typeof blockAuthoringDataSchema>;

// ═══════════════════════════════════════════════════════════════════════════
// Helpers
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Find a parameter by id (the synthesised stable id, usually = handle).
 */
export function findAuthoringParameterById(
  data: BlockAuthoringData,
  id: string,
): BlockAuthoringParameter | undefined {
  return data.parameters.find((p) => p.id === id);
}

/**
 * Find an action by id.
 */
export function findAuthoringActionById(
  data: BlockAuthoringData,
  id: string,
): BlockAuthoringAction | undefined {
  return data.actions.find((a) => a.id === id);
}

/**
 * List actions linked to a given parameter (forward edges).
 */
export function findActionsForParameter(
  data: BlockAuthoringData,
  parameterId: string,
): BlockAuthoringAction[] {
  return data.actions.filter((a) => a.parameterId === parameterId);
}
